import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

/**
 * Cinematic sound provider — synthesises tiny static/glitch effects on the
 * fly via the WebAudio API so we don't ship audio files. Honours the user's
 * mute preference (persisted in localStorage) and the OS-level
 * `prefers-reduced-motion` setting.
 */
const SoundContext = createContext(null);
const STORAGE_KEY = "gd_muted";

export function SoundProvider({ children }) {
  const [muted, setMuted] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage?.getItem(STORAGE_KEY) === "1";
  });
  const ctxRef = useRef(null);

  // Lazy-create the AudioContext on first user gesture. Browsers block it
  // before any interaction.
  const ensureCtx = useCallback(() => {
    if (typeof window === "undefined") return null;
    if (ctxRef.current && ctxRef.current.state !== "closed") {
      // Browsers (especially Safari/iOS) keep the AudioContext in `suspended`
      // state after the page has been idle or until the user has interacted
      // at least once. Without this resume, scheduled tension/unveil sounds
      // are silently swallowed and Federico hears nothing.
      if (ctxRef.current.state === "suspended") {
        ctxRef.current.resume().catch(() => {});
      }
      return ctxRef.current;
    }
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      ctxRef.current = new Ctx();
      if (ctxRef.current.state === "suspended") {
        ctxRef.current.resume().catch(() => {});
      }
      return ctxRef.current;
    } catch (err) {
      // AudioContext blocked or unsupported (older Safari, strict CSP).
      // Fall back to silent mode — sound is purely decorative.
      if (process.env.NODE_ENV === "development") {
        console.debug("[sound] AudioContext init failed:", err);
      }
      return null;
    }
  }, []);

  // Global audio unlock: hook a one-shot listener on the first pointer/key
  // event anywhere in the document so the AudioContext is alive before the
  // countdown reaches its final 10 seconds. Without this, the user has to
  // click *something* between page load and timer-zero, otherwise the boom
  // is muted.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const unlock = () => {
      ensureCtx();
      // Warm up SpeechSynthesis on the first user gesture so the very first
      // "Vault is open." utterance doesn't drop its first syllable (Safari
      // and Chrome both need a primed engine before they speak reliably).
      if (typeof window !== "undefined" && window.speechSynthesis) {
        try {
          window.speechSynthesis.getVoices(); // primes the voice list async
          const ping = new SpeechSynthesisUtterance("");
          ping.volume = 0;
          window.speechSynthesis.speak(ping);
        } catch {
          /* speechSynthesis blocked — voice will degrade gracefully */
        }
      }
    };
    const opts = { once: true, passive: true };
    window.addEventListener("pointerdown", unlock, opts);
    window.addEventListener("keydown", unlock, opts);
    window.addEventListener("touchstart", unlock, opts);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
  }, [ensureCtx]);

  useEffect(() => {
    try {
      window.localStorage?.setItem(STORAGE_KEY, muted ? "1" : "0");
    } catch (err) {
      // localStorage quota exceeded or disabled — non-fatal, mute state
      // simply won't persist across sessions.
      if (process.env.NODE_ENV === "development") {
        console.debug("[sound] could not persist mute state:", err);
      }
    }
  }, [muted]);

  const toggleMute = useCallback(() => setMuted((m) => !m), []);

  /**
   * Short white-noise burst with a band-pass filter. Used for the boot
   * loader, glitch overlays and vault transitions. Volume stays soft but
   * audible (the previous 6% peak was too quiet on most laptops, so we
   * bumped it to ~14%).
   */
  const playStatic = useCallback(
    (durationMs = 600) => {
      if (muted) return;
      const ctx = ensureCtx();
      if (!ctx) return;
      const duration = durationMs / 1000;
      const buffer = ctx.createBuffer(1, ctx.sampleRate * duration, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < data.length; i += 1) {
        data[i] = (Math.random() * 2 - 1) * 0.6;
      }
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      const filter = ctx.createBiquadFilter();
      filter.type = "bandpass";
      filter.frequency.value = 1800;
      filter.Q.value = 0.7;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.14, ctx.currentTime + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration);
      src.connect(filter).connect(gain).connect(ctx.destination);
      src.start();
      src.stop(ctx.currentTime + duration + 0.05);
    },
    [muted, ensureCtx]
  );

  /** Low cinematic boom — used when the vault opens. */
  const playBoom = useCallback(() => {
    if (muted) return;
    const ctx = ensureCtx();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(80, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(35, ctx.currentTime + 0.7);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.32, ctx.currentTime + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.9);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 1);
  }, [muted, ensureCtx]);

  /** Short Jarvis-style "tick" — used for every status check or HUD
   *  element that ticks into place. Two-tone (1200 → 1800Hz) sine blip. */
  const playTick = useCallback(() => {
    if (muted) return;
    const ctx = ensureCtx();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(1200, ctx.currentTime);
    osc.frequency.linearRampToValueAtTime(1800, ctx.currentTime + 0.08);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.18, ctx.currentTime + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.13);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.18);
  }, [muted, ensureCtx]);

  // Hold a reference to the active tension drone so we can start it once
  // when the countdown enters its final 10s window and stop it cleanly at
  // zero or when the user navigates away.
  const droneRef = useRef(null);

  /**
   * Tension drone — a low, dissonant pad that fades in the moment the
   * countdown crosses below 10 seconds and stays underneath every war-drum
   * pulse until the explosion. Built from two detuned saw oscillators
   * (35Hz / 35.6Hz) into a band-pass filter with rising resonance — that
   * gives the "something is about to break" feeling Federico asked for.
   */
  const startTensionDrone = useCallback(() => {
    if (muted) return;
    const ctx = ensureCtx();
    if (!ctx) return;
    if (droneRef.current) return; // already running
    const now = ctx.currentTime;

    const oscA = ctx.createOscillator();
    oscA.type = "sawtooth";
    oscA.frequency.value = 35;

    const oscB = ctx.createOscillator();
    oscB.type = "sawtooth";
    oscB.frequency.value = 35.6; // tiny detune for beating dissonance

    const filt = ctx.createBiquadFilter();
    filt.type = "bandpass";
    filt.frequency.setValueAtTime(120, now);
    filt.frequency.linearRampToValueAtTime(420, now + 9.5);
    filt.Q.setValueAtTime(3, now);
    filt.Q.linearRampToValueAtTime(14, now + 9.5);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.085, now + 0.6);
    gain.gain.linearRampToValueAtTime(0.22, now + 9.5);

    oscA.connect(filt);
    oscB.connect(filt);
    filt.connect(gain).connect(ctx.destination);
    oscA.start(now);
    oscB.start(now);

    droneRef.current = { oscA, oscB, gain, ctx };
  }, [muted, ensureCtx]);

  const stopTensionDrone = useCallback(() => {
    const d = droneRef.current;
    if (!d) return;
    const { oscA, oscB, gain, ctx } = d;
    const now = ctx.currentTime;
    try {
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(gain.gain.value, now);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
      oscA.stop(now + 0.4);
      oscB.stop(now + 0.4);
    } catch {
      /* nodes already torn down */
    }
    droneRef.current = null;
  }, []);

  /**
   * Final-countdown war-drum pulse — fired once per second during the
   * last 10s on top of the tension drone. Dark and weighty: a sub-bass
   * thump with a metallic transient overlay. `step` 0..9 controls volume
   * and pitch, so it crescendos as the timer reaches zero.
   *
   * Federico's brief: "dark, like something is about to explode".
   */
  const playTension = useCallback(
    (step = 0) => {
      if (muted) return;
      const ctx = ensureCtx();
      if (!ctx) return;
      const now = ctx.currentTime;
      const intensity = step / 9; // 0..1

      // 1. Sub-bass war drum thump (50→25Hz body)
      const sub = ctx.createOscillator();
      sub.type = "sine";
      const subStart = 50 + step * 2; // 50→68Hz
      sub.frequency.setValueAtTime(subStart, now);
      sub.frequency.exponentialRampToValueAtTime(subStart * 0.5, now + 0.45);
      const subGain = ctx.createGain();
      subGain.gain.setValueAtTime(0, now);
      subGain.gain.linearRampToValueAtTime(0.42 + intensity * 0.15, now + 0.012);
      subGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.55);
      sub.connect(subGain).connect(ctx.destination);
      sub.start(now);
      sub.stop(now + 0.6);

      // 2. Metallic transient — short filtered noise click at attack
      const noiseLen = 0.12;
      const buf = ctx.createBuffer(1, ctx.sampleRate * noiseLen, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < d.length; i += 1) d[i] = (Math.random() * 2 - 1) * 0.9;
      const noise = ctx.createBufferSource();
      noise.buffer = buf;
      const hp = ctx.createBiquadFilter();
      hp.type = "highpass";
      hp.frequency.value = 1200;
      const noiseGain = ctx.createGain();
      noiseGain.gain.setValueAtTime(0, now);
      noiseGain.gain.linearRampToValueAtTime(0.06 + intensity * 0.04, now + 0.004);
      noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);
      noise.connect(hp).connect(noiseGain).connect(ctx.destination);
      noise.start(now);
      noise.stop(now + noiseLen);

      // 3. Dissonant low harmonic — a soft minor-second on top of the
      //    drone for the "things are bending out of shape" feel.
      const harm = ctx.createOscillator();
      harm.type = "sawtooth";
      harm.frequency.value = 165 + step * 6; // rising
      const harmFilt = ctx.createBiquadFilter();
      harmFilt.type = "lowpass";
      harmFilt.frequency.value = 900;
      harmFilt.Q.value = 6;
      const harmGain = ctx.createGain();
      harmGain.gain.setValueAtTime(0, now);
      harmGain.gain.linearRampToValueAtTime(0.04 + intensity * 0.05, now + 0.04);
      harmGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);
      harm.connect(harmFilt).connect(harmGain).connect(ctx.destination);
      harm.start(now);
      harm.stop(now + 0.55);
    },
    [muted, ensureCtx]
  );

  // Pick a cinematic English female voice (Samantha/Karen/Moira/Allison
  // sound best on Apple devices; on Android/Windows we fall back to the
  // first `en-*` female voice the OS exposes).
  const pickVoice = useCallback(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) return null;
    const voices = window.speechSynthesis.getVoices();
    if (!voices.length) return null;
    const preferredNames = [
      "Samantha", // macOS / iOS — warm, neutral
      "Allison",  // macOS premium
      "Karen",    // AU English
      "Moira",    // IE English
      "Tessa",    // ZA English
      "Serena",   // UK
      "Google UK English Female",
      "Microsoft Aria Online",
      "Microsoft Jenny Online",
    ];
    for (const name of preferredNames) {
      const v = voices.find((x) => x.name === name);
      if (v) return v;
    }
    return (
      voices.find((v) => /en[-_]/i.test(v.lang) && /female|samantha|aria|jenny|karen/i.test(v.name)) ||
      voices.find((v) => /en[-_]/i.test(v.lang)) ||
      voices[0]
    );
  }, []);

  /**
   * Spoken announcement — the cinematic payoff at countdown zero.
   * "Vault is open." in a slowed, slightly lower female voice. Replaces
   * the old explosion sound: Federico wanted a voice instead of a boom.
   *
   * We also warm up the speech engine on first user-gesture (see the
   * global unlock useEffect) so the very first invocation doesn't drop
   * the first syllable on Safari.
   */
  const playVoiceVaultOpen = useCallback(() => {
    if (muted) return;
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    try {
      const synth = window.speechSynthesis;
      // Cancel any pending utterance so it speaks immediately.
      synth.cancel();
      const utt = new SpeechSynthesisUtterance("Vault is open.");
      utt.lang = "en-US";
      utt.rate = 0.78;   // slow, dramatic
      utt.pitch = 0.95;  // slightly lower than default for gravitas
      utt.volume = 1.0;
      const voice = pickVoice();
      if (voice) utt.voice = voice;
      synth.speak(utt);
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.debug("[sound] speechSynthesis failed:", err);
      }
    }
  }, [muted, pickVoice]);

  // Backward-compat stub. The cinematic explosion was replaced by a voice
  // announcement ("Vault is open.") — kept as a no-op so any caller still
  // wired to playUnveil continues to render without errors.
  const playUnveil = useCallback(() => {}, []);

  const value = {
    muted,
    toggleMute,
    setMuted,
    playStatic,
    playBoom,
    playTick,
    playTension,
    startTensionDrone,
    stopTensionDrone,
    playUnveil,
    playVoiceVaultOpen,
  };

  return <SoundContext.Provider value={value}>{children}</SoundContext.Provider>;
}

export const useSound = () => {
  const ctx = useContext(SoundContext);
  if (!ctx) throw new Error("useSound must be used within SoundProvider");
  return ctx;
};
